import React, { useEffect, useState, useContext } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import toast from "react-hot-toast";
import { HiOutlineArrowLeft } from "react-icons/hi";
import * as Yup from "yup";
import { FloatingInput, Loader } from "../../Components/Common";
import {
  getBillById,
  createBill,
  fetchCategories,
  updateBillStatus,
  deleteBill,
} from "../../Services/BillService";
import { getSites } from "../../Services/siteService";
import {
  FORM_MODES,
  VALIDATION_MESSAGES,
  FORM_TITLES,
  BUTTON_TEXT,
  FIELD_LABELS,
  TOAST_MESSAGES,
} from "./constants";
import { GET_ALL_PAGINATION } from "../../Constants/Strings";
import { UserContext } from "../../Context";
import * as routes from "../../routes";
import { FiTrash2 } from "react-icons/fi";
import { HiViewfinderCircle } from "react-icons/hi2";
import { BiHide } from "react-icons/bi";
import { extractErrorMessage } from "../../Utils/extractErrorMessage";

const BillOperations = () => {
  const { billType } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { nonStaffAccess } = useContext(UserContext);

  const [mode, setMode] = useState(FORM_MODES.ADD);
  const [initialValues, setInitialValues] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sites, setSites] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showBill, setShowBill] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const isViewMode = mode === FORM_MODES.VIEW;
  const isPending =
    initialValues?.status === "PENDING_L1_APPROVAL" ||
    initialValues?.status === "PENDING_L2_APPROVAL";

  const filters = location.state?.filters || {
    status: "",
    date: null,
    user: "",
    type: "",
    site: ""
  };

  useEffect(() => {
    if (location.pathname.includes("view")) setMode(FORM_MODES.VIEW);
    else setMode(FORM_MODES.ADD);
  }, [location.pathname]);

  const fetchInitialData = async () => {
    try {
      const res = await getSites(GET_ALL_PAGINATION);
      setSites(res?.data?.results || []);
    } catch (error) {
      const msg = extractErrorMessage(error) || TOAST_MESSAGES.SITES_LOAD_ERROR;
      toast.error(msg);
      setSites([]);
    }

    try {
      const res = await fetchCategories({ type: billType?.toUpperCase() });
      setCategories(res?.data?.results || []);
    } catch (error) {
      const msg = extractErrorMessage(error) || TOAST_MESSAGES.CATEGORIES_LOAD_ERROR;
      toast.error(msg);
      setCategories([]);
    }
  };

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (mode === FORM_MODES.VIEW) {
      const id = location.pathname.split("/").pop();
      setLoading(true);
      getBillById(id)
        .then((res) => setInitialValues(res.data))
        .catch(() => toast.error(TOAST_MESSAGES.BILL_LOAD_ERROR))
        .finally(() => setLoading(false));
    } else {
      const today = new Date().toISOString().split("T")[0];
      setInitialValues({
        site: "",
        date: today,
        amount: "",
        category: "",
        type: billType,
        user: "",
        bill_file: null,
      });
    }
  }, [mode, location.pathname]);

  if (
    loading ||
    !initialValues ||
    !Array.isArray(sites) ||
    !Array.isArray(categories)
  ) {
    return (
      <div className="flex justify-center pt-10">
        <Loader size={4} />
      </div>
    );
  }

  const validationSchema = Yup.object().shape({
    site: Yup.string().required(VALIDATION_MESSAGES.SITE_REQUIRED),
    date: Yup.date().required(VALIDATION_MESSAGES.DATE_REQUIRED),
    amount: Yup.number().required(VALIDATION_MESSAGES.AMOUNT_REQUIRED),
    type: Yup.string().required(VALIDATION_MESSAGES.TYPE_REQUIRED),
    category: Yup.string().required(VALIDATION_MESSAGES.CATEGORY_REQUIRED),
    bill_file: Yup.mixed().nullable(),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const formData = new FormData();
      formData.append("site", values.site);
      formData.append("date", values.date);
      formData.append("amount", values.amount);
      formData.append("type", values.type);
      formData.append("category", values.category);
      formData.append("user_name", values.user_name);
      if (values.bill_file instanceof File) {
        formData.append("bill_file", values.bill_file);
      }

      const response = await createBill(formData);

      if (response?.status === 201 || response?.status === 200) {
        toast.success(TOAST_MESSAGES.BILL_ADD_SUCCESS);
        navigate(routes.LIST_BILLS);
      } else {
        const msg = extractErrorMessage(response) || TOAST_MESSAGES.BILL_ADD_ERROR;
        toast.error(msg);
      }
    } catch (error) {
      const msg = extractErrorMessage(error) || TOAST_MESSAGES.BILL_ADD_ERROR;
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusChange = async (status) => {
    try {
      const id = initialValues?.id;
      const response = await updateBillStatus(id, { action: status });

      if (response?.status === 200) {
        toast.success(TOAST_MESSAGES.STATUS_UPDATE_SUCCESS(status));
        navigate(routes.LIST_BILLS);
      } else {
        const msg = extractErrorMessage(response) || TOAST_MESSAGES.BILL_STATUS_ERROR;
        toast.error(msg);
      }
    } catch (error) {
      const msg = extractErrorMessage(error) || TOAST_MESSAGES.BILL_STATUS_ERROR;
      toast.error(msg);
    }
  };

  const handleBillDelete = async (id) => {
    try {
      await deleteBill(id);
      toast.success(TOAST_MESSAGES.BILL_DELETE);
      navigate(routes.LIST_BILLS);
    } catch (error) {
      const msg = extractErrorMessage(error) || TOAST_MESSAGES.BILL_DELETE_ERROR;
      toast.error(msg);
    }
  };


  const handleBack = () => {
    navigate(routes.LIST_BILLS, { state: { filters } });
  };

  if (loading || !initialValues)
    return (
      <div className="flex justify-center pt-10">
        <Loader size={4} />
      </div>
    );

  const commonInput =
    "w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-secondary";

  return (
    <div className="w-full max-w-xl mx-auto bg-white p-6 md:p-8 rounded-lg shadow-md mt-8">
      <div className="flex items-center justify-between mb-6">
        <button onClick={handleBack} className="text-primary text-2xl">
          <HiOutlineArrowLeft />
        </button>
        <h2 className="text-xl font-bold text-primary text-center flex-1">
          {isViewMode ? FORM_TITLES.VIEW : FORM_TITLES.ADD}
        </h2>
        <div className="w-6" />
      </div>

      <Formik
        initialValues={initialValues}
        validationSchema={isViewMode ? null : validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ isSubmitting, setFieldValue }) => (
          <Form className="flex flex-col gap-1">
            {/* Add Mode */}
            {!isViewMode && (
              <>
                {/* Site */}
                <div className="">
                  <label className="text-sm font-semibold text-secondary mb-1 block">
                    {FIELD_LABELS.SITE}
                  </label>
                  <Field
                    as="select"
                    name="site"
                    className={commonInput}
                    disabled={sites.length === 0}
                  >
                    <option value="">
                      {sites.length === 0
                        ? "No sites available"
                        : "Select site"}
                    </option>
                    {sites.map((site) => (
                      <option key={site.id} value={site.id}>
                        {site.name}
                      </option>
                    ))}
                  </Field>

                  <ErrorMessage
                    name="site"
                    component="div"
                    className="text-red-500 text-sm mt-1"
                  />
                </div>

                {/* Date */}
                <div className="">
                  <label className="text-sm font-semibold text-secondary mb-1 block">
                    {FIELD_LABELS.DATE}
                  </label>
                  <Field
                    type="date"
                    name="date"
                    className={commonInput}
                    disabled={isSubmitting}
                  />
                  <ErrorMessage
                    name="date"
                    component="div"
                    className="text-red-500 text-sm mt-1"
                  />
                </div>

                {/* Amount */}
                <div className="">
                  <label className="text-sm font-semibold text-secondary mb-1 block">
                    {FIELD_LABELS.AMOUNT}
                  </label>
                  <Field
                    type="number"
                    name="amount"
                    className={commonInput}
                    disabled={isSubmitting}
                  />
                  <ErrorMessage
                    name="amount"
                    component="div"
                    className="text-red-500 text-sm mt-1"
                  />
                </div>

                {/* Type */}
                <div className="">
                  <label className="text-sm font-semibold text-secondary mb-1 block">
                    {FIELD_LABELS.TYPE}
                  </label>
                  <Field name="type">
                    {({ field }) => (
                      <select
                        {...field}
                        onChange={async (e) => {
                          const selectedType = e.target.value;
                          setFieldValue("type", selectedType);

                          try {
                            const response = await fetchCategories({
                              type: selectedType,
                            }); // pass type
                            setCategories(response.data?.results || []);
                          } catch (err) {
                            toast.error(TOAST_MESSAGES.CATEGORIES_LOAD_ERROR);
                          }
                        }}
                        className={`${commonInput} bg-gray-100 text-gray-600 border-gray-200 cursor-not-allowed`}
                        disabled={true}
                      >
                        {" "}
                        <option value="">
                          {" "}
                          {billType.charAt(0).toUpperCase() +
                            billType.slice(1).toLowerCase()}{" "}
                        </option>
                        <option value="PURCHASE">Purchase</option>
                        <option value="REIMBURSEMENT">Reimbursement</option>
                      </select>
                    )}
                  </Field>
                  <ErrorMessage
                    name="type"
                    component="div"
                    className="text-red-500 text-sm mt-1"
                  />
                </div>

                {/* Category */}
                <div className="">
                  <label className="text-sm font-semibold text-secondary mb-1 block">
                    {FIELD_LABELS.CATEGORY}
                  </label>
                  <Field
                    as="select"
                    name="category"
                    className={commonInput}
                    disabled={isSubmitting || categories.length === 0}
                  >
                    <option value="">
                      {categories.length === 0
                        ? "No Category available"
                        : "Select Catagory"}
                    </option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </Field>
                  <ErrorMessage
                    name="category"
                    component="div"
                    className="text-red-500 text-sm mt-1"
                  />
                </div>

                {/* File Upload */}
                <div className=" flex flex-col md:flex-row items-start md:items-center gap-2 md:gap-4">
                  <label className="text-sm font-semibold text-secondary whitespace-nowrap">
                    {FIELD_LABELS.PHOTO}
                  </label>

                  <div className="flex flex-col md:flex-row items-start md:items-center gap-2 md:gap-4 w-full">
                    <input
                      type="file"
                      name="bill_file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.currentTarget.files[0];
                        if (file) {
                          setSelectedFile(file);
                          const isImage = file.type.startsWith("image/");
                          const url = URL.createObjectURL(file);
                          setPreviewUrl(url);
                          if (!isImage) {
                            alert(VALIDATION_MESSAGES.IMAGE_VALIDATION);
                            e.target.value = "";
                            return;
                          }
                          setFieldValue("bill_file", file);
                        }
                      }}
                      className="text-sm border rounded px-3 py-2 w-full md:w-auto"
                      disabled={isSubmitting}
                    />

                    {previewUrl && (
                      <button
                        type="button"
                        className="text-primary underline flex items-center gap-1 text-sm"
                        onClick={() => setShowPreview(true)}
                      >
                        <HiViewfinderCircle size={18} /> View Photo
                      </button>
                    )}
                  </div>
                </div>

                {showPreview && previewUrl && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-4 rounded-lg max-w-[90%] max-h-[90%] overflow-auto relative">
                      <button
                        className="absolute top-2 right-2 text-red-500 text-xl"
                        onClick={() => setShowPreview(false)}
                      >
                        &times;
                      </button>

                      {/* File type preview logic */}
                      {(() => {
                        const fileType = selectedFile?.type || "";
                        if (fileType.startsWith("image/")) {
                          return (
                            <img
                              src={previewUrl}
                              alt="Preview"
                              className="max-w-full max-h-[80vh] object-contain"
                            />
                          );
                        } else if (fileType === "application/pdf") {
                          return (
                            <iframe
                              src={previewUrl}
                              title="PDF Preview"
                              className="w-full h-[80vh]"
                            />
                          );
                        } else if (
                          fileType.startsWith("text/") ||
                          selectedFile?.name?.endsWith(".txt")
                        ) {
                          return (
                            <iframe
                              src={previewUrl}
                              title="Text Preview"
                              className="w-full h-[80vh]"
                            />
                          );
                        } else {
                          return (
                            <div className="text-center text-gray-600">
                              Cannot preview this file type.
                              <a
                                href={previewUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block mt-2 text-blue-600 underline"
                              >
                                Open in new tab
                              </a>
                            </div>
                          );
                        }
                      })()}
                    </div>
                  </div>
                )}
              </>
            )}
            {/* View Mode */}
            {isViewMode && (
              <>
                <div className="">
                  <FloatingInput
                    label={FIELD_LABELS.SITE}
                    name="site_name"
                    value={initialValues.site_name || "-"}
                    disabled
                  />
                </div>

                <div className="">
                  <FloatingInput
                    label={FIELD_LABELS.DATE}
                    name="date"
                    value={initialValues.date}
                    disabled
                  />
                </div>

                {nonStaffAccess && (
                  <div className="">
                    <FloatingInput
                      label={FIELD_LABELS.USER}
                      name="user_name"
                      value={initialValues.user_name}
                      disabled
                    />
                  </div>
                )}

                <div className="">
                  <FloatingInput
                    label={FIELD_LABELS.AMOUNT}
                    name="amount"
                    value={initialValues.amount}
                    disabled
                  />
                </div>

                <div className="">
                  <FloatingInput
                    label={FIELD_LABELS.TYPE}
                    name="type"
                    value={
                      initialValues.type &&
                      initialValues.type.charAt(0).toUpperCase() +
                      initialValues.type.slice(1).toLowerCase()
                    }
                    disabled
                  />
                </div>

                <div className="">
                  <FloatingInput
                    label={FIELD_LABELS.CATEGORY}
                    name="category_name"
                    value={initialValues.category_name || "-"}
                    disabled
                  />
                </div>

                <div className="">
                  <FloatingInput
                    label={FIELD_LABELS.STATUS}
                    name="status"
                    value={(initialValues.status || "N/A")
                      .toLowerCase()
                      .split("_")
                      .map(
                        (word) => word.charAt(0).toUpperCase() + word.slice(1)
                      )
                      .join(" ")}
                    disabled
                  />
                </div>

                <div className="">
                  <label className="block text-sm font-semibold mb-1">
                    {FIELD_LABELS.PHOTO}
                  </label>
                  <button
                    type="button"
                    className="text-primary underline flex items-center gap-1 mb-1"
                    onClick={() => setShowBill(!showBill)}
                  >
                    {showBill ? (
                      <div className="flex items-center gap-1">
                        <BiHide />
                        <span>Hide Photo</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1">
                        <HiViewfinderCircle />
                        View Photo
                      </div>
                    )}
                  </button>
                  {initialValues.bill_file ? (
                    showBill && (
                      <img
                        src={initialValues.bill_file}
                        alt="Bill"
                        className="w-full max-h-96 rounded border object-contain"
                      />
                    )
                  ) : (
                    <p className="text-gray-500 text-sm">No image uploaded</p>
                  )}
                </div>
              </>
            )}

            {/* Buttons */}
            {!isViewMode && (
              <div className="flex justify-between gap-3 mt-4">
                <button
                  type="button"
                  onClick={() => navigate(routes.LIST_BILLS)}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded font-semibold text-base"
                  disabled={isSubmitting}
                >
                  {BUTTON_TEXT.CANCEL}
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-primary hover:bg-secondary text-white px-5 py-2 rounded-md"
                >
                  {isSubmitting ? BUTTON_TEXT.SUBMITTING : BUTTON_TEXT.SUBMIT}
                </button>
              </div>
            )}

            {isViewMode && (
              <div
                className={`flex ${nonStaffAccess && isPending
                  ? "justify-center gap-4"
                  : "justify-center"
                  } mt-6`}
              >
                {nonStaffAccess && isPending && (
                  <>
                    <button
                      type="button"
                      onClick={() => handleStatusChange("approved")}
                      className="bg-green-600 hover:bg-green-700 text-white px-5 py-2 rounded-md"
                    >
                      {BUTTON_TEXT.APPROVE}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleStatusChange("rejected")}
                      className="bg-red-600 hover:bg-red-700 text-white px-5 py-2 rounded-md"
                    >
                      {BUTTON_TEXT.REJECT}
                    </button>
                  </>
                )}

                <button
                  type="button"
                  className={`cursor-pointer ${isPending
                    ? "text-red-600"
                    : "text-gray-400 cursor-not-allowed"
                    } ${nonStaffAccess && isPending ? "" : "mx-auto"} pr-5 py-2`}
                  onClick={() => {
                    if (!isPending) return;

                    const confirmed = window.confirm(
                      TOAST_MESSAGES.DELETE_ALERT
                    );
                    if (confirmed) {
                      handleBillDelete(initialValues?.id);
                    }
                  }}
                  title={BUTTON_TEXT.DELETE_BILL}
                >
                  <FiTrash2 className="w-7 h-7" />
                </button>
              </div>
            )}
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default BillOperations;
